// Sidebar chọn nội dung (Chương > Bài > Dạng)

import React, { useState, useEffect } from 'react'
import { ChevronDown, ChevronRight, Plus, Check, Loader2 } from 'lucide-react'
import { DANG_TOAN_MAPPING, getDangToanOptions, getTenBai, getChuongName, getBaiListForChuong } from '../../config/dang-toan-mapping'
import { bankQuestionService } from '../../services/bankQuestionService_moi'
import type { ContentNode } from '../../types/exam-matrix'

interface ContentSidebarProps {
  lop: number
  selectedChuong: number[]
  onAddDang: (node: ContentNode) => void
  addedDangs: Set<string>
}

export const ContentSidebar: React.FC<ContentSidebarProps> = ({
  lop,
  selectedChuong,
  onAddDang,
  addedDangs
}) => {
  const [expandedChuong, setExpandedChuong] = useState<Set<number>>(new Set())
  const [expandedBai, setExpandedBai] = useState<Set<string>>(new Set())
  const [questionCounts, setQuestionCounts] = useState<Record<string, number>>({})
  const [loading, setLoading] = useState(false)
  
  // Load question counts khi component mount hoặc selectedChuong thay đổi
  useEffect(() => {
    if (selectedChuong.length > 0) {
      loadQuestionCounts()
    }
  }, [lop, selectedChuong])
  
  const loadQuestionCounts = async () => {
    setLoading(true)
    const counts: Record<string, number> = {}
    
    try {
      for (const chuong of selectedChuong) {
        const baiList = getBaiListForChuong(lop, chuong)
        
        for (const bai of baiList) {
          const key = `L${lop}_C${chuong}_B${bai}`
          
          try {
            const available = await bankQuestionService.getAvailableQuestionsForRow(lop, chuong, bai)
            const total = Object.values(available).reduce((sum, val) => sum + val, 0)
            counts[key] = total
          } catch (error) {
            console.error(`Error loading questions for ${key}:`, error)
            counts[key] = 0
          }
        }
      }
    } finally {
      setQuestionCounts(counts)
      setLoading(false)
    }
  }
  
  const toggleChuong = (chuong: number) => {
    const newSet = new Set(expandedChuong)
    if (newSet.has(chuong)) {
      newSet.delete(chuong)
    } else {
      newSet.add(chuong)
    }
    setExpandedChuong(newSet)
  }
  
  const toggleBai = (key: string) => {
    const newSet = new Set(expandedBai)
    if (newSet.has(key)) {
      newSet.delete(key)
    } else {
      newSet.add(key)
    }
    setExpandedBai(newSet)
  }
  
  const getDangKey = (lop: number, chuong: number, bai: number, dang: string) => {
    return `L${lop}_C${chuong}_B${bai}_${dang}`
  }
  
  const isDangAdded = (lop: number, chuong: number, bai: number, dang: string) => {
    return addedDangs.has(getDangKey(lop, chuong, bai, dang))
  }
  
  return (
    <div className="content-sidebar h-full flex flex-col bg-white border-r">
      <div className="p-4 border-b bg-gradient-to-r from-blue-50 to-indigo-50">
        <h3 className="font-semibold text-gray-800">Chọn nội dung</h3>
        <p className="text-xs text-gray-600 mt-1">Click vào dạng toán để thêm</p>
        {loading && (
          <div className="flex items-center gap-2 mt-2 text-sm text-blue-600">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span>Đang tải...</span>
          </div>
        )}
      </div>
      
      <div className="flex-1 overflow-y-auto p-2">
        {selectedChuong.length === 0 ? (
          <div className="text-center text-gray-500 py-8 px-4">
            <p className="text-sm">Chưa chọn chương nào</p>
            <p className="text-xs mt-2">Vui lòng chọn chương ở phía trên</p>
          </div>
        ) : (
          selectedChuong.map(chuong => (
            <ChuongNode
              key={chuong}
              lop={lop}
              chuong={chuong}
              isExpanded={expandedChuong.has(chuong)}
              onToggle={() => toggleChuong(chuong)}
              expandedBai={expandedBai}
              onToggleBai={toggleBai}
              onAddDang={onAddDang}
              questionCounts={questionCounts}
              isDangAdded={isDangAdded}
            />
          ))
        )}
      </div>
    </div>
  )
}

// Component cho Chương
const ChuongNode: React.FC<{
  lop: number
  chuong: number
  isExpanded: boolean
  onToggle: () => void
  expandedBai: Set<string>
  onToggleBai: (key: string) => void
  onAddDang: (node: ContentNode) => void
  questionCounts: Record<string, number>
  isDangAdded: (lop: number, chuong: number, bai: number, dang: string) => boolean
}> = ({ lop, chuong, isExpanded, onToggle, expandedBai, onToggleBai, onAddDang, questionCounts, isDangAdded }) => {
  const baiList = getBaiListForChuong(lop, chuong)
  const chuongName = getChuongName(lop, chuong)
  
  return (
    <div className="mb-2">
      <div
        className="flex items-center px-2 py-2 hover:bg-blue-50 rounded cursor-pointer transition-colors"
        onClick={onToggle}
      >
        {isExpanded ? (
          <ChevronDown className="w-4 h-4 text-blue-600 mr-1 flex-shrink-0" />
        ) : (
          <ChevronRight className="w-4 h-4 text-gray-400 mr-1 flex-shrink-0" />
        )}
        <span className="font-semibold text-sm text-gray-800">Chương {chuong}: {chuongName}</span>
      </div>
      
      {isExpanded && (
        <div className="ml-4 mt-1">
          {baiList.map(bai => (
            <BaiNode
              key={bai}
              lop={lop}
              chuong={chuong}
              bai={bai}
              isExpanded={expandedBai.has(`L${lop}_C${chuong}_B${bai}`)}
              onToggle={() => onToggleBai(`L${lop}_C${chuong}_B${bai}`)}
              onAddDang={onAddDang}
              questionCount={questionCounts[`L${lop}_C${chuong}_B${bai}`] || 0}
              isDangAdded={isDangAdded}
            />
          ))}
        </div>
      )}
    </div>
  )
}

// Component cho Bài
const BaiNode: React.FC<{
  lop: number
  chuong: number
  bai: number
  isExpanded: boolean
  onToggle: () => void
  onAddDang: (node: ContentNode) => void
  questionCount: number
  isDangAdded: (lop: number, chuong: number, bai: number, dang: string) => boolean
}> = ({ lop, chuong, bai, isExpanded, onToggle, onAddDang, questionCount, isDangAdded }) => {
  const tenBai = getTenBai(lop, chuong, bai)
  const cacDang = getDangToanOptions(lop, chuong, bai)
  const hasQuestions = questionCount > 0
  
  return (
    <div className="mb-1">
      <div
        className="flex items-center justify-between px-2 py-1.5 hover:bg-gray-50 rounded cursor-pointer transition-colors"
        onClick={onToggle}
      >
        <div className="flex items-center flex-1 min-w-0">
          {isExpanded ? (
            <ChevronDown className="w-3 h-3 text-gray-400 mr-1 flex-shrink-0" />
          ) : (
            <ChevronRight className="w-3 h-3 text-gray-400 mr-1 flex-shrink-0" />
          )}
          <span className="text-sm text-gray-700 truncate">Bài {bai}: {tenBai}</span>
        </div>
        <span className={`ml-2 text-xs px-2 py-0.5 rounded flex-shrink-0 ${
          hasQuestions ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
        }`}>
          {questionCount}
        </span>
      </div>
      
      {isExpanded && cacDang.length > 0 && (
        <div className="ml-4 mt-1 space-y-0.5">
          {cacDang.map((dang, index) => (
            <DangNode
              key={index}
              lop={lop}
              chuong={chuong}
              bai={bai}
              dang={dang}
              onAdd={onAddDang}
              isAdded={isDangAdded(lop, chuong, bai, dang)}
              hasQuestions={hasQuestions}
            />
          ))}
        </div>
      )}
      
      {isExpanded && cacDang.length === 0 && (
        <div className="ml-4 text-xs text-gray-400 py-1">
          Chưa có dạng toán
        </div>
      )}
    </div>
  )
}

// Component cho Dạng toán
const DangNode: React.FC<{
  lop: number
  chuong: number
  bai: number
  dang: string
  onAdd: (node: ContentNode) => void
  isAdded: boolean
  hasQuestions: boolean
}> = ({ lop, chuong, bai, dang, onAdd, isAdded, hasQuestions }) => {
  const handleAdd = () => {
    if (!isAdded) {
      onAdd({
        type: 'dang',
        lop,
        chuong,
        bai,
        dang,
        label: dang,
        hasQuestions
      })
    }
  }
  
  return (
    <div
      className={`
        flex items-center justify-between px-2 py-1.5 rounded text-sm group
        ${isAdded 
          ? 'bg-blue-50 text-blue-700 cursor-default' 
          : 'hover:bg-gray-50 cursor-pointer text-gray-700'
        }
      `}
      onClick={handleAdd}
    >
      <span className="flex-1 text-xs">{dang}</span>
      {isAdded ? (
        <Check className="w-4 h-4 text-blue-600 flex-shrink-0" />
      ) : (
        <Plus className="w-4 h-4 text-gray-400 group-hover:text-blue-600 flex-shrink-0" />
      )}
    </div>
  )
}

